import { useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import Table from '@components/table';
import styles from './styles.module.scss';
import illustration from '@svg/illustrations/no_requests.svg';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import ChatDrawer from '@sections/help_center/chat_drawer';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiSupportService } from '@api';
import Skeleton from '@components/common/skeleton';
import { ETicketStatus } from '@xeppt/xeppt-sdk/types';
import Status from '@components/common/status';
import {
    capitalizeFirstLetter,
    formatDate,
    getTicketStatus,
    getTicketStatusVariant
} from '@utils/index';
import { routes } from '@const/routes';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import Info from '@components/common/info';
import { useLocales } from '@hooks/helpers/useLocales';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { ENewNotificationType } from '@enum';

const HelpCenterLayout = () => {
    const { submitLocale } = useLocales();
    const [id] = useQueryParams(['id']);
    const [chosenTicket, setChosenTicket] = useState<string>();
    const columns = [
        {
            label: 'Subject',
            key: 'subject'
        },
        {
            label: 'ID',
            key: 'id'
        },
        {
            label: 'Created',
            key: 'created_at',
            render: (value: Date) => value && formatDate(value, 'MM.DD.YYYY, HH:mm')
        },
        {
            label: 'Updated',
            key: 'updated_at',
            render: (value: Date) => value && formatDate(value, 'MM.DD.YYYY, HH:mm')
        },
        {
            label: 'Status',
            key: 'status',
            render: (val: ETicketStatus) => (
                <Status variant={getTicketStatusVariant(getTicketStatus(val))}>
                    {capitalizeFirstLetter(getTicketStatus(val))}
                </Status>
            )
        },
        {
            key: 'id',
            isRightAlignment: true,
            render: (val: string) => {
                return (
                    <Info variant="left" content="View details">
                        <Button
                            leftIcon="open_eye"
                            className={styles.action}
                            onClick={() => {
                                setIsOpen(true);
                                setChosenTicket(val);
                            }}
                        />
                    </Info>
                );
            }
        }
    ];
    const [isOpen, setIsOpen] = useState(false);
    const { handleBack } = useNavigationContext();

    useEffect(() => {
        if (id) {
            setIsOpen(true);
            setChosenTicket(id);
        }
    }, [id]);

    const {
        data: ticketsData,
        isLoading,
        handleRequest: refetchTickets
    } = useApiQuery({
        method: () => apiSupportService.getTicketsList(),
        isInitialLoading: true,
        notificationType: ENewNotificationType.SUPPORT
    });

    const {
        data: ticketData,
        handleRequest: refetchTicket,
        removeResponse: removeTicketResponse
    } = useApiQuery({
        method: () => apiSupportService.getTicketById(chosenTicket || ''),
        isInitialRequest: false,
        condition: !!chosenTicket,
        deps: [chosenTicket]
    });

    const { handleOpenSubmitRequestModal } = useHelpCenterModals(refetchTickets);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.dashboard)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Help center</Typography>
            </div>
            <div className={styles.description_wrapper}>
                <Typography variant="h5">My requests</Typography>
                <Button variant="primary" onClick={() => handleOpenSubmitRequestModal(undefined)}>
                    {submitLocale('submit_request')}
                </Button>
            </div>
            {isLoading ? (
                <>
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                </>
            ) : (
                <>
                    {(ticketsData?.total || 0) !== 0 ? (
                        <>
                            {/*<div className={styles.filters}>*/}
                            {/*    <Input*/}
                            {/*        className={styles.search}*/}
                            {/*        full*/}
                            {/*        leftIcon="search"*/}
                            {/*        placeholder="Search by request ID or subject..."*/}
                            {/*    />*/}
                            {/*    <RangeDatePicker*/}
                            {/*        rootClassName={styles.range}*/}
                            {/*        full*/}
                            {/*        placeholder="Select a date range created"*/}
                            {/*    />*/}
                            {/*</div>*/}
                            <Table
                                columns={columns}
                                rows={ticketsData?.data || []}
                                isActions
                                emptyDescription="No requests yet"
                            />
                        </>
                    ) : (
                        <div className={styles.empty_wrapper}>
                            <img src={illustration} alt="No requests yet" />
                            <Typography variant="body3">
                                It seems like everything is fine so far... Requests you submit will
                                appear right here
                            </Typography>
                            <Button
                                variant="primary"
                                onClick={() => handleOpenSubmitRequestModal(undefined)}>
                                {submitLocale('submit_request')}
                            </Button>
                        </div>
                    )}
                </>
            )}
            <ChatDrawer
                isOpen={isOpen}
                ticketData={ticketData}
                refetchTicket={refetchTicket}
                onClose={() => {
                    setIsOpen(false);
                    removeTicketResponse();
                    setChosenTicket(undefined);
                }}
            />
        </motion.div>
    );
};

export default HelpCenterLayout;
